import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Privacy Policy | Best Christmas Gift Reviews';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Read the Best Christmas Gift Reviews privacy policy to understand how we collect, use, and protect your personal information.');

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/privacy-policy#webpage`,
      url: `${SITE_URL}/privacy-policy`,
      name: 'Privacy Policy',
      description: 'Read the Best Christmas Gift Reviews privacy policy to understand how we collect, use, and protect your personal information.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${SITE_URL}/privacy-policy` },
        ],
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'jsonld-privacy';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('jsonld-privacy');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-[#1B4332] hover:text-[#8B2635] transition-colors mb-6">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-[#1B4332] mb-4">Privacy Policy</h1>
            <p className="text-gray-500">Last Updated: January 2025</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Best Christmas Gift Reviews ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you visit our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Information We Collect</h2>
              <h3 className="text-lg font-semibold text-[#1B4332] mb-3">Automatically Collected Information</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Browser type and version</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Operating system</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>IP address (anonymized)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Pages visited and time spent on pages</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Referring website addresses</span>
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-[#1B4332] mb-3">Information You Provide</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Email address (when subscribing to our newsletter)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Contact information (when reaching out to us)</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">How We Use Your Information</h2>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>To improve our website and user experience</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>To send newsletters and updates (with your consent)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>To respond to your inquiries</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>To analyze website traffic and trends</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small files stored on your device that help us understand how you use our website.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You can control cookie settings through your browser. However, disabling cookies may affect some website functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our website may contain links to third-party websites, including Amazon and other retail partners. We are not responsible for the privacy practices of these external sites.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We use analytics services (such as Google Analytics) to understand website usage. These services may collect information about your visits to our site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We implement appropriate security measures to protect your personal information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">You have the right to:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Access the personal data we hold about you</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Request correction of inaccurate data</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Request deletion of your data</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Unsubscribe from marketing communications</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this privacy policy from time to time. Any changes will be posted on this page with an updated revision date.
              </p>
            </section>

            <section className="bg-[#FFF9F5] rounded-xl p-6 border border-[#D4AF37]/20">
              <h2 className="text-xl font-bold text-[#1B4332] mb-3">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have questions about this privacy policy, please contact us at{' '}
                <a href="mailto:hello@bestchristmasgiftreviews.com" className="text-[#8B2635] hover:underline">
                  hello@bestchristmasgiftreviews.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
